import React, { useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import cx from "classnames"
import { ArrowLeft, ChevronDown, Circle, Settings, Shield } from "react-feather"
import { toRouteSlug } from "../../../utils/routeSlug"
import { getProjectIcon } from "./projectIcons"
import s from "./Sidebar.module.scss"

/** "thinkgas-com" -> "Thinkgas Com" */
const toTitle = (value) =>
  String(value ?? "")
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim()

/**
 * Does `pathname` sit on `path` — the route itself or anything nested under it?
 *
 * Prefix-matched rather than compared outright so a detail route
 * ("/user-access-panel/42") keeps its parent highlighted; the trailing slash
 * stops "/user-access-panel" from claiming "/user-access-panel-archive".
 */
const isPathActive = (pathname, path) => {
  if (!path || !pathname) return false
  return pathname === path || pathname.startsWith(`${path}/`)
}

/**
 * Project/module navigation.
 *
 * Purely presentational: it receives an already-shaped `projects` array and
 * reports clicks through `onNavigate`. Auth and routing stay in the container
 * (`AppShell`), which keeps this testable and reusable for other verticals.
 *
 * Groups auto-expand for the active project — and, in the footer nav, for the
 * active route — and stay user-toggleable after.
 */
const Sidebar = ({
  projects = [],
  activeProject,
  activeModule,
  activePath = "",
  collapsed = false,
  onNavigate,
  onRequestExpand,
  brand,
  footerItems = [],
  className
}) => {
  // Slugged, not just lowercased: the item keys below are slugs, and an active
  // project/module read off a legacy "%20" URL has to match them to highlight.
  const normalizedActiveProject = toRouteSlug(activeProject)
  const normalizedActiveModule = toRouteSlug(activeModule)

  // Which groups the user has open. Seeded from the active project.
  const [openKeys, setOpenKeys] = useState(() =>
    normalizedActiveProject ? [normalizedActiveProject] : []
  )

  // Follow route changes without discarding groups the user opened manually.
  useEffect(() => {
    if (!normalizedActiveProject) return
    setOpenKeys((keys) => (keys.includes(normalizedActiveProject) ? keys : [...keys, normalizedActiveProject]))
  }, [normalizedActiveProject])

  const toggle = (key) =>
    setOpenKeys((keys) => (keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key]))

  /**
   * Footer entries resolve their own active state from the route.
   *
   * The project groups above match on project/module slugs, which footer items
   * don't have — they're arbitrary routes. Matching them on `path` here keeps
   * the highlight rule in one component rather than asking each caller to work
   * it out.
   */
  const footer = useMemo(
    () =>
      footerItems.map((item) => {
        const children = (item.children || []).map((child) => ({
          ...child,
          isActive: isPathActive(activePath, child.path)
        }))

        return {
          ...item,
          children,
          // A group with an active child is itself active — the same rule the
          // project groups follow.
          isActive: isPathActive(activePath, item.path) || children.some((c) => c.isActive)
        }
      }),
    [footerItems, activePath]
  )

  // Open the group holding the active route, so landing on it directly (a deep
  // link, a refresh) shows the highlighted child rather than a closed group.
  const activeFooterKey = footer.find((item) => item.isActive && item.children.length)?.key

  useEffect(() => {
    if (!activeFooterKey) return
    setOpenKeys((keys) => (keys.includes(activeFooterKey) ? keys : [...keys, activeFooterKey]))
  }, [activeFooterKey])

  const items = useMemo(
    () =>
      // Every `key` here becomes a URL segment, so both levels are slugged:
      // a project named "Production Toolkit" would otherwise emit
      // "/production%20toolkit/basejig", and the encoded segment matches
      // nothing downstream. The `label` fields keep the readable names.
      projects.map((p) => {
        const key = toRouteSlug(p.project)
        return {
          key,
          label: p.label || toTitle(p.project),
          // Per-project glyph; the generic circle stands in for anything the
          // icon set doesn't know yet, so a new project still renders.
          Icon: getProjectIcon(key) ?? Circle,
          modules: (p.modules || []).map((m) => {
            const name = typeof m === "string" ? m : m.module_name
            return { key: toRouteSlug(name), label: String(name ?? "").toUpperCase() }
          })
        }
      }),
    [projects]
  )

  return (
    <aside className={cx(s.sidebar, { [s.collapsed]: collapsed }, className)}>
      {brand}

      <nav className={s.nav} aria-label="Projects">
        {items.length === 0 && <p className={s.empty}>No projects assigned</p>}

        {items.map((project) => {
          const isActiveProject = project.key === normalizedActiveProject
          const isOpen = openKeys.includes(project.key)
          const hasModules = project.modules.length > 0

          return (
            <div className={s.group} key={project.key}>
              <button
                type="button"
                className={cx(s.groupBtn, { [s.groupActive]: isActiveProject })}
                aria-expanded={hasModules ? isOpen : undefined}
                onClick={() => {
                  // Collapsed, the module list is hidden, so toggling a group
                  // would do nothing visible — expand the rail first and open
                  // the group the user actually aimed at.
                  if (collapsed) {
                    onRequestExpand?.()
                    if (hasModules) {
                      setOpenKeys((keys) =>
                        keys.includes(project.key) ? keys : [...keys, project.key]
                      )
                      return
                    }
                  }
                  if (hasModules) toggle(project.key)
                  else onNavigate?.({ project: project.key })
                }}
              >
                <span className={s.groupIcon} aria-hidden="true">
                  <project.Icon size={17} />
                </span>
                <span className={s.groupLabel}>{project.label}</span>
                {hasModules && (
                  <span className={cx(s.chevron, { [s.chevronOpen]: isOpen })} aria-hidden="true">
                    <ChevronDown size={15} />
                  </span>
                )}
                {/* Only rendered as a rail tooltip; hidden when expanded. */}
                <span className={s.tip} role="tooltip">
                  {project.label}
                </span>
              </button>

              {hasModules && isOpen && (
                <ul className={s.moduleList}>
                  {project.modules.map((module) => {
                    const isActive = isActiveProject && module.key === normalizedActiveModule
                    return (
                      <li key={`${project.key}-${module.key}`}>
                        <button
                          type="button"
                          className={cx(s.moduleBtn, { [s.moduleActive]: isActive })}
                          aria-current={isActive ? "page" : undefined}
                          onClick={() => onNavigate?.({ project: project.key, module: module.key })}
                        >
                          <span className={s.moduleArrow} aria-hidden="true">
                            <ArrowLeft size={13} />
                          </span>
                          <span className={s.moduleLabel}>{module.label}</span>
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          )
        })}
      </nav>

      {footerItems.length > 0 && (
        <div className={s.footerNav}>
          <nav aria-label="Settings">
            {footer.map((item) => {
              const isOpen = openKeys.includes(item.key)
              const hasChildren = item.children.length > 0
              const Icon = item.icon === "shield" ? Shield : Settings

              return (
                <div className={s.group} key={item.key}>
                  <button
                    type="button"
                    className={cx(s.groupBtn, { [s.groupActive]: item.isActive })}
                    aria-expanded={hasChildren ? isOpen : undefined}
                    onClick={() => {
                      // Same rule as the project groups above: a collapsed rail
                      // can't show children, so expand before opening one.
                      if (collapsed && hasChildren) {
                        onRequestExpand?.()
                        setOpenKeys((keys) =>
                          keys.includes(item.key) ? keys : [...keys, item.key]
                        )
                        return
                      }
                      if (hasChildren) toggle(item.key)
                      else item.onClick?.()
                    }}
                  >
                    <span className={s.groupIcon} aria-hidden="true">
                      <Icon size={15} />
                    </span>
                    <span className={s.groupLabel}>{item.label}</span>
                    {hasChildren && (
                      <span className={cx(s.chevron, { [s.chevronOpen]: isOpen })} aria-hidden="true">
                        <ChevronDown size={15} />
                      </span>
                    )}
                    <span className={s.tip} role="tooltip">
                      {item.label}
                    </span>
                  </button>

                  {hasChildren && isOpen && (
                    <ul className={s.moduleList}>
                      {item.children.map((child) => (
                        <li key={child.key}>
                          <button
                            type="button"
                            className={cx(s.moduleBtn, { [s.moduleActive]: child.isActive })}
                            aria-current={child.isActive ? "page" : undefined}
                            onClick={child.onClick}
                          >
                            <span className={s.moduleArrow} aria-hidden="true">
                              <ArrowLeft size={13} />
                            </span>
                            <span className={s.moduleLabel}>{child.label}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )
            })}
          </nav>
        </div>
      )}
    </aside>
  )
}

Sidebar.propTypes = {
  projects: PropTypes.arrayOf(
    PropTypes.shape({
      project: PropTypes.string,
      label: PropTypes.string,
      modules: PropTypes.array
    })
  ),
  activeProject: PropTypes.string,
  activeModule: PropTypes.string,
  /** Current `location.pathname`, used to highlight the footer entries. */
  activePath: PropTypes.string,
  collapsed: PropTypes.bool,
  /** ({ project, module }) => void — container turns this into a route change. */
  onNavigate: PropTypes.func,
  /** Called when a collapsed rail needs to expand to be usable. */
  onRequestExpand: PropTypes.func,
  /** Brand block rendered above the nav (logo + wordmark). */
  brand: PropTypes.node,
  footerItems: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.oneOf(["settings", "shield"]),
      /** Route this entry leads to — matched against `activePath`. */
      path: PropTypes.string,
      onClick: PropTypes.func,
      children: PropTypes.array
    })
  ),
  className: PropTypes.string
}

export { toTitle }
export default Sidebar
